// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import {
  useCallback,
  useMemo,
  useRef,
  useState,
  type FocusEvent,
  type MouseEvent,
} from 'react';
import { useTranslation } from 'react-i18next';
import type { IlliquidLinkableProduct } from '../../utils/illiquid-linked-products';

export interface UseIlliquidAssetAccountProductSelectorProps {
  readonly availableProducts: readonly IlliquidLinkableProduct[];
  readonly onLink: (iban: string) => void | Promise<void>;
}

const normalizeIban = (value: string) => value.replace(/\s+/g, '').toLowerCase();

export const matchesProductQuery = (
  product: IlliquidLinkableProduct,
  rawQuery: string,
): boolean => {
  const query = rawQuery.trim().toLowerCase();

  if (!query) return true;

  const nameMatch = (product.name ?? '').toLowerCase().includes(query);

  const bankMatch = (product.institution_name ?? '').toLowerCase().includes(query);

  const ibanMatch = normalizeIban(product.iban).includes(normalizeIban(query));

  return nameMatch || bankMatch || ibanMatch;
};

export const useIlliquidAssetAccountProductSelector = ({
  availableProducts,
  onLink,
}: UseIlliquidAssetAccountProductSelectorProps) => {
  const { t } = useTranslation();

  const inputRef = useRef<HTMLInputElement>(null);

  const suppressOpenRef = useRef(false);

  const [query, setQuery] = useState('');

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const open = Boolean(anchorEl);

  const filtered = useMemo(
    () => availableProducts.filter(product => matchesProductQuery(product, query)),
    [availableProducts, query],
  );

  const onOpen = useCallback((event: MouseEvent<HTMLElement> | FocusEvent) => {
    if (suppressOpenRef.current) return;

    setAnchorEl(event.currentTarget as HTMLElement);
  }, []);

  const onClose = useCallback(() => {
    setAnchorEl(null);
    setQuery('');
  }, []);

  const onChangeQuery = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setQuery(event.target.value);

      if (!anchorEl && event.currentTarget) {
        setAnchorEl(event.currentTarget);
      }
    },
    [anchorEl],
  );

  const onSelectProduct = useCallback(
    (iban: string) => {
      suppressOpenRef.current = true;

      onClose();

      inputRef.current?.blur();

      onLink(iban);

      window.setTimeout(() => {
        suppressOpenRef.current = false;
      }, 0);
    },
    [onClose, onLink],
  );

  return {
    t,
    inputRef,
    query,
    open,
    anchorEl,
    filtered,
    availableProducts,
    onOpen,
    onClose,
    onChangeQuery,
    onSelectProduct,
  } as const;
};
